import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShippingLoadDetail } from 'src/app/shared/models';
import { PostingLoadProgress } from '../../../store/reducers/shipping-load-detail.reducer';
import { ShippingState, getShippingPostingProgress } from '../../../store';

interface PostingProgressRow {
  loadId: string;
  referenceLoadDisplay: string;
  origin: string;
  destination: string;
  status: 'pending' | 'in-progress' | 'success' | 'error';
  errorMessages: string[];
}

@Component({
  selector: 'kbxl-shipping-post-loads-progress',
  templateUrl: './shipping-post-loads-progress.component.html',
  styleUrls: ['./shipping-post-loads-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShippingPostLoadsProgressComponent implements OnInit {
  rows$: Observable<PostingProgressRow[]>;
  allComplete$: Observable<boolean>;
  summary$: Observable<{ total: number; success: number; error: number; pending: number; inProgress: number }>;

  private loads: ShippingLoadDetail[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private shippingStore: Store<ShippingState>
  ) {
    this.loads = (this.config?.data?.loads || []) as ShippingLoadDetail[];
  }

  ngOnInit(): void {
    const progress$ = this.shippingStore.pipe(select(getShippingPostingProgress));

    this.rows$ = progress$.pipe(
      map((progress: { [loadId: string]: PostingLoadProgress }) =>
        this.loads.map((load) => {
          const p = progress?.[load.loadId];
          const firstStop = (load.loadStops || [])[0];
          const lastStop = (load.loadStops || [])[(load.loadStops || []).length - 1];
          return {
            loadId: load.loadId,
            referenceLoadDisplay: load.referenceLoadDisplay || load.loadId,
            origin: firstStop ? `${firstStop.city || ''}, ${firstStop.state || ''}` : '',
            destination: lastStop ? `${lastStop.city || ''}, ${lastStop.state || ''}` : '',
            status: p ? p.status : 'pending',
            errorMessages: p?.errorMessages || [],
          } as PostingProgressRow;
        })
      )
    );

    this.summary$ = this.rows$.pipe(
      map((rows) => ({
        total: rows.length,
        success: rows.filter((r) => r.status === 'success').length,
        error: rows.filter((r) => r.status === 'error').length,
        pending: rows.filter((r) => r.status === 'pending').length,
        inProgress: rows.filter((r) => r.status === 'in-progress').length,
      }))
    );

    this.allComplete$ = this.summary$.pipe(map((s) => s.total > 0 && s.pending === 0 && s.inProgress === 0));
  }

  finish(): void {
    this.ref.close();
  }

  statusIcon(status: PostingProgressRow['status']): string {
    switch (status) {
      case 'success':
        return 'pi pi-check-circle text-success';
      case 'error':
        return 'pi pi-times-circle text-danger';
      case 'in-progress':
        return 'pi pi-spin pi-spinner text-info';
      case 'pending':
      default:
        return 'pi pi-clock text-muted';
    }
  }

  statusLabel(status: PostingProgressRow['status']): string {
    switch (status) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Failed';
      case 'in-progress':
      case 'pending':
      default:
        return 'Pending';
    }
  }
}
